/**
 * PillTabs — pill-container tab group with colored status dots and counts.
 *
 * Used for status/confidence segmented controls where each tab conveys a
 * level (success/warning/error/neutral) via a coloured dot and count.
 *
 * @param {Array}  tabs        - [{ key, label, count, level }]
 *                               level: "success" | "warning" | "error" | "neutral"
 *                               (also accepts "high" → success, "mid" → warning,
 *                               "low" → error for back-compat with ConfidenceTabs)
 * @param {string} activeKey   - key of the currently active tab
 * @param {func}   onChange    - called with the key when a tab is clicked
 * @param {bool}   fill        - if true, tabs flex-grow to fill container width
 * @param {string} countStyle  - "text" (default): "(N)" in colored text next to label
 *                               "badge": N inside a coloured pill (colours only on active tab)
 */

const LEVEL_ALIAS = { high: 'success', mid: 'warning', low: 'error' };

export const PillTabs = ({
    tabs = [],
    activeKey,
    onChange,
    fill = false,
    countStyle = 'text',
}) => {
    return (
        <div className={`pill-tabs${fill ? ' pill-tabs--fill' : ''}`} role="tablist">
            {tabs.map((tab) => {
                const level = LEVEL_ALIAS[tab.level] || tab.level;
                const isActive = tab.key === activeKey;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`pill-tabs__item${isActive ? ' pill-tabs__item--active' : ''}`}
                        onClick={() => onChange(tab.key)}
                    >
                        {level && <span className={`pill-tabs__dot pill-tabs__dot--${level}`} />}
                        <span className="pill-tabs__label body-s-bold">{tab.label}</span>
                        {tab.count !== undefined && countStyle === 'text' && (
                            <span className={`pill-tabs__count-text body-s-bold${level ? ` pill-tabs__count-text--${level}` : ''}`}>
                                ({tab.count})
                            </span>
                        )}
                        {tab.count !== undefined && countStyle === 'badge' && (
                            <span className={`pill-tabs__count-badge support-m${isActive && level ? ` pill-tabs__count-badge--${level}` : ''}`}>
                                ({tab.count})
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};
